/**
 * Pantone Formula Guide — Solid Coated (GP1601B) palette (frontend mirror).
 *
 * Single source of truth for chart colours on the React side. Mirrors the
 * Python ``backend/services/palette.py`` so the in-app dashboards match
 * the printed PDF reports exactly (Sprint 1–4 brand alignment).
 *
 * Convention identical to the backend:
 *   PMS_<number>_C — Pantone Matching System swatch (Solid Coated).
 *   SEMANTIC_*     — the role the colour plays in the AHA app.
 *
 * Hex values are the published sRGB equivalents of the named PMS chips
 * — reviewers can verify any swatch against their physical fan deck.
 */

// ── Raw PMS swatches ─────────────────────────────────────────────────
export const PMS_533_C        = '#1F2A44';   // Deep navy
export const PMS_542_C        = '#7BAFD4';   // Carolina blue — primary accent
export const PMS_2925_C       = '#009CDE';   // Bright sky blue — interactive
export const PMS_320_C        = '#009CA6';   // Teal — chart primary
export const PMS_347_C        = '#00843D';   // Confident green — chart secondary
export const PMS_2725_C       = '#5C68C5';   // Soft violet — chart accent
export const PMS_1375_C       = '#FFA300';   // Saturated amber — warning
export const PMS_186_C        = '#C8102E';   // Pantone alert red — error / critical
export const PMS_130_C        = '#F2A900';   // Major / warning amber
export const PMS_COOL_GRAY_8C = '#888B8D';   // Neutral mid-gray
export const PMS_COOL_GRAY_11C = '#53565A';  // Dark gray — axis labels / dim text
export const PMS_354_C        = '#00B140';   // Bright certification green
export const PMS_285_C        = '#0072CE';   // Reference blue — info
export const PMS_656_C        = '#DBE2E9';   // Pale blue-gray — light surface
export const PMS_COOL_GRAY_3C = '#C7C9C7';   // Soft border / grid line
export const PMS_1245_C       = '#A07400';   // Deep ochre — advisory text
export const PMS_426_C        = '#1D1D1B';   // Rich black — body text

// ── Semantic roles ───────────────────────────────────────────────────
export const BRAND_PRIMARY   = PMS_533_C;
export const ACCENT          = PMS_542_C;
export const INTERACTIVE     = PMS_2925_C;

export const SEV_CRITICAL    = PMS_186_C;
export const SEV_MAJOR       = PMS_130_C;
export const SEV_MINOR       = PMS_COOL_GRAY_8C;

export const STATUS_PASS     = PMS_354_C;
export const STATUS_WARN     = PMS_130_C;
export const STATUS_FAIL     = PMS_186_C;
export const STATUS_INFO     = PMS_285_C;
export const STATUS_REVIEW   = PMS_542_C;   // HITL_FLAGGED uses Carolina blue

// ── Chart roles (taken verbatim from the dashboard mock-up legend) ──
export const CHART_PRIMARY   = PMS_320_C;   // teal
export const CHART_SECONDARY = PMS_347_C;   // green
export const CHART_ACCENT    = PMS_2725_C;  // violet
export const CHART_WARNING   = PMS_1375_C;  // amber
export const CHART_ERROR     = PMS_186_C;   // red

// Categorical palette — used when we need ≥6 distinct chart colours
// (e.g. per-category bar chart with many BEP categories). Ordered so
// the most common categories pick up the most distinguishable swatches.
export const CATEGORICAL_PALETTE = [
  CHART_PRIMARY,
  CHART_SECONDARY,
  CHART_ACCENT,
  CHART_WARNING,
  CHART_ERROR,
  PMS_285_C,
  PMS_542_C,
  PMS_COOL_GRAY_8C,
];

// Maps used by both the project-level and per-audit dashboards.
export const STATUS_COLORS = {
  PASS:   STATUS_PASS,
  WARN:   STATUS_WARN,
  FAIL:   STATUS_FAIL,
  REVIEW: STATUS_REVIEW,
};
export const CERT_COLORS = {
  SELF_CERTIFIED: STATUS_PASS,
  HITL_FLAGGED:   ACCENT,
  REJECTED:       STATUS_FAIL,
};
export const SEVERITY_COLORS = {
  critical: SEV_CRITICAL,
  major:    SEV_MAJOR,
  minor:    SEV_MINOR,
};

export function severityColor(severity) {
  return SEVERITY_COLORS[(severity || '').toLowerCase()] || SEV_MINOR;
}

export function categoricalColor(index) {
  return CATEGORICAL_PALETTE[index % CATEGORICAL_PALETTE.length];
}
